import {
  EpisodesGridTextAlignment,
  EpisodesGridTextPosition,
  ISettingsColors,
  ISettingsFonts,
  ISettingsMain,
  DEFAULT_SETTING_MAIN,
} from '@wix/podcasts-common/dist/settings';
import { TPADeviceType } from '@wix/podcasts-common/dist/tpa';
import { css } from 'emotion';
import {
  FIXED_FONT_SIZE,
  LINE_HEIGHT,
} from '../../../../../lib/styles/dynamicStyleConstants';
import getFontStyles from '../../../../../lib/styles/getFontStyles';
import getLineClampStyles from '../../../../../lib/styles/getLineClampStyles';

const LINES_ALLOWED = {
  MAIN_EPISODE_TITLE: 2,
  MAIN_EPISODE_DESCRIPTION: 3,
};

interface IEpisodesGridItemStyles {
  container: string;
  episodeInner: string;
  imageContainer: string;
  infoContainer: string;
  episodeTitle: string;
  episodeLabel: string;
  episodeMetaInfo: string;
  episodeDescription: string;
  episodeListenNow: string;
}

interface IGetStylesProps {
  colors: ISettingsColors['main'];
  fonts: ISettingsFonts['main'];
  settings: ISettingsMain;
  deviceType: TPADeviceType;
}

function getTextAlignment(value: EpisodesGridTextAlignment) {
  switch (value) {
    case EpisodesGridTextAlignment.CENTER:
      return 'center';
    case EpisodesGridTextAlignment.RIGHT:
      return 'right';
    case EpisodesGridTextAlignment.LEFT:
    default:
      return 'left';
  }
}

function getStyles(props: IGetStylesProps): IEpisodesGridItemStyles {
  const { deviceType, colors, fonts, settings } = props;
  const textAlign = getTextAlignment(settings.episodesGridTextAlignment);

  return {
    container: css({
      ...{
        [EpisodesGridTextPosition.BELOW]: {
          background: colors.gridItemBackgroundOpacityColor,
        },
        [EpisodesGridTextPosition.ON]: {},
      }[settings.episodesGridTextPosition],

      padding: `${settings.gridItemBorderWidth}px`,
    }),
    episodeInner: css({
      outlineStyle: 'solid',
      outlineWidth: `${settings.gridItemBorderWidth}px`,
      outlineColor: colors.gridItemBorderOpacityColor,
    }),

    imageContainer: css(
      {
        [EpisodesGridTextPosition.BELOW]: {},
        [EpisodesGridTextPosition.ON]: {
          '&:after': { background: colors.gridItemOverlayOpacityColor },
        },
      }[settings.episodesGridTextPosition],
    ),
    infoContainer: css({
      boxSizing: 'border-box',
      margin:
        settings.episodesGridTextPosition === EpisodesGridTextPosition.BELOW
          ? settings.episodesGridTextIndent
          : DEFAULT_SETTING_MAIN.episodesGridTextIndent,
      marginTop: '24px',
    }),
    episodeTitle: css({
      textAlign,
      ...getFontStyles(fonts.episodeTitleFont, {
        lineHeight: LINE_HEIGHT.TITLE,
      }),
      ...{
        [EpisodesGridTextPosition.BELOW]: {
          color: colors.episodeTitleColor,
        },
        [EpisodesGridTextPosition.ON]: {
          color: colors.episodeTextOnGridTitleColor,
        },
      }[settings.episodesGridTextPosition],
      ...{
        [TPADeviceType.DESKTOP]: {},
        [TPADeviceType.MOBILE]: { fontSize: 16 },
      }[deviceType],
      '&:hover': { color: colors.episodeButtonColor },
      '&:active': { color: colors.episodeButtonColor },
      ...getLineClampStyles(
        deviceType === TPADeviceType.MOBILE
          ? { size: 16 }
          : fonts.episodeTitleFont,
        LINE_HEIGHT.TITLE,
        LINES_ALLOWED.MAIN_EPISODE_TITLE,
      ),
    }),
    episodeLabel: css({
      ...getFontStyles(fonts.episodeDescriptionFont, {
        fontSize: FIXED_FONT_SIZE.META_INFO,
        lineHeight: 1.14,
      }),
    }),
    episodeMetaInfo: css({
      textAlign,
      ...getFontStyles(fonts.episodeDescriptionFont!),
      ...{
        [EpisodesGridTextPosition.BELOW]: {
          color: colors.episodeDescriptionColor,
          '& > *': { opacity: 0.6 },
        },
        [EpisodesGridTextPosition.ON]: {
          color: colors.episodeTextOnGridDescriptionColor,
        },
      }[settings.episodesGridTextPosition],
      ...{
        [TPADeviceType.DESKTOP]: { fontSize: FIXED_FONT_SIZE.META_INFO },
        [TPADeviceType.MOBILE]: { fontSize: 12 },
      }[deviceType],
    }),
    episodeDescription: css({
      textAlign,
      ...getFontStyles(fonts.episodeDescriptionFont!),
      ...{
        [EpisodesGridTextPosition.BELOW]: {
          color: colors.episodeDescriptionColor,
        },
        [EpisodesGridTextPosition.ON]: {
          color: colors.episodeTextOnGridDescriptionColor,
        },
      }[settings.episodesGridTextPosition],
      ...{
        [TPADeviceType.DESKTOP]: {},
        [TPADeviceType.MOBILE]: { fontSize: 14 },
      }[deviceType],
      ...getLineClampStyles(
        deviceType === TPADeviceType.MOBILE
          ? { size: 14 }
          : fonts.episodeDescriptionFont,
        LINE_HEIGHT.DEFAULT,
        LINES_ALLOWED.MAIN_EPISODE_DESCRIPTION,
      ),
    }),
    episodeListenNow: css({
      ...getFontStyles(fonts.episodeDescriptionFont!),
      ...{
        [EpisodesGridTextPosition.BELOW]: {
          color: colors.episodeDescriptionColor,
        },
        [EpisodesGridTextPosition.ON]: {
          color: colors.episodeTextOnGridDescriptionColor,
        },
      }[settings.episodesGridTextPosition],
      fontSize: 16,
      lineHeight: '32px',
    }),
  };
}

export default getStyles;
export { IEpisodesGridItemStyles };
